/**
 * Renderização compartilhada do COMPROVANTE de solicitação (o "story" 9:16
 * que o colaborador compartilha). Extraído de Comprovante.jsx para reuso na
 * Gestão (comprovantes de todas as solicitações de um lote — baixar/imprimir).
 *
 * Exporta:
 * - `ComprovanteCardView` — o card visual (renderizar dentro de CompOffscreen);
 * - `jpgDeElemento(el)`  — captura o elemento via html2canvas → Blob JPEG,
 *   com os clamps de canvas que evitam captura em branco no iOS Safari;
 * - `baixarBlob(blob, nome)` — download programático.
 */
import React from "react";
import html2canvas from "html2canvas";

import {
  MOTIVOS_PECA, MOTIVO_REQUISICAO_LABEL,
} from "../../../services/solicitacoes";
import {
  formatarCorMdf, formatarDataHora, formatarMedidaCompacta,
} from "./utils";
import {
  CompLinhas, CompMarca, CompMotivo, CompNumero, CompPecaBloco, CompPecas,
  CompRodape, ComprovanteCard,
} from "./styles";

// Fallback de rótulo quando o payload não traz o *_display do backend
// (ex.: resposta de criação consumida direto pela tela de sucesso).
const MOTIVO_PECA_LABEL = Object.fromEntries(
  MOTIVOS_PECA.map((m) => [m.value, m.label]),
);

export function baixarBlob(blob, nomeArquivo) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeArquivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/**
 * Captura um elemento como JPEG (Blob). Limita a escala para o canvas nunca
 * passar da área/lado seguros do browser (iOS Safari) — senão a captura
 * voltaria EM BRANCO silenciosamente. Com poucas peças a escala fica em 2.
 */
export async function jpgDeElemento(el) {
  const w = el.offsetWidth || 540;
  const h = el.offsetHeight || 960;
  const MAX_LADO = 4096; // limite por lado conservador (aparelhos antigos)
  const MAX_AREA = 16000000; // limite de área de canvas do iOS Safari
  const scale = Math.max(
    1,
    Math.min(2, MAX_LADO / Math.max(w, h), Math.sqrt(MAX_AREA / (w * h))),
  );
  const canvas = await html2canvas(el, {
    scale,
    backgroundColor: "#ffffff",
    logging: false,
  });
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.92),
  );
  if (!blob) throw new Error("canvas.toBlob retornou vazio");
  return blob;
}

function LinhaDado({ rotulo, valor }) {
  if (!valor) return null;
  return (
    <div>
      <span className="rotulo">{rotulo}</span>
      <span className="valor">{valor}</span>
    </div>
  );
}

// Comprovante enxuto: quantidade + descrição + medida (CxLxE) —
// "2 descrição - cor" numa linha (quebra se precisar), sem fita.
function BlocoPeca({ peca }) {
  const medida = formatarMedidaCompacta(peca);
  const cor = formatarCorMdf(peca.material_nome, peca.espessura_mm);
  const qtd = Number(peca.quantidade) > 0 ? Number(peca.quantidade) : 1;
  return (
    <CompPecaBloco>
      <div className="titulo-linha">
        <span className="peca-txt">
          <span className="qtd">{qtd}</span>
          <span className="desc">{peca.descricao || "Peça"}</span>
          {cor && <span className="cor"> - {cor}</span>}
        </span>
        {medida && <span className="medida">{medida}</span>}
      </div>
    </CompPecaBloco>
  );
}

function BlocoItem({ item }) {
  const q = Number(item.quantidade) > 0 ? Number(item.quantidade) : 1;
  const qtd = `${q}${item.unidade_medida ? ` ${item.unidade_medida}` : ""}`;
  const sub = [
    item.medida ? `Medida ${item.medida}` : "",
    item.codigo ? `Cód. ${item.codigo}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <CompPecaBloco>
      <div className="titulo-linha">
        <span className="peca-txt">
          <span className="qtd">{qtd}</span>
          <span className="desc">{item.descricao}</span>
        </span>
      </div>
      {sub && <div className="subcor">{sub}</div>}
    </CompPecaBloco>
  );
}

/**
 * Motivos distintos da solicitação, na ordem em que aparecem nas peças/itens.
 * O motivo é coletado POR peça (assistência) / POR item (RQA); na prática todas
 * compartilham o mesmo — por isso deduplicamos em vez de repetir N vezes.
 * Itens sem motivo (RQI) simplesmente não entram.
 */
function motivosDaSolicitacao(blocos, ehAssistencia) {
  const vistos = new Set();
  const lista = [];
  (blocos || []).forEach((b) => {
    const rotulo = String(
      (ehAssistencia
        ? b.motivo_categoria_display
          || MOTIVO_PECA_LABEL[b.motivo_categoria]
          || b.motivo_categoria
        : b.motivo_display
          || MOTIVO_REQUISICAO_LABEL[b.motivo]
          || b.motivo) || "",
    ).trim();
    if (!rotulo) return;
    const detalhe = String(b.motivo_detalhe || "").trim();
    const texto = detalhe ? `${rotulo} — ${detalhe}` : rotulo;
    if (vistos.has(texto)) return;
    vistos.add(texto);
    lista.push(texto);
  });
  return lista;
}

/**
 * Card completo do comprovante. `cardRef` aponta para o nó capturado pelo
 * html2canvas; `solicitanteFallback` cobre o pós-envio (payload ainda sem
 * criado_por_nome resolvido); `loteNumero` é preenchido quando o comprovante
 * sai pela impressão de um lote de MDF — aí o número do lote vem NA FRENTE do
 * número da solicitação (é por ele que o pessoal do corte acha o serviço).
 */
export function ComprovanteCardView({
  solicitacao,
  familia,
  solicitanteFallback = "",
  loteNumero = "",
  cardRef,
}) {
  const ehAssistencia = familia === "assistencia";
  const blocos = ehAssistencia
    ? solicitacao.pecas || []
    : solicitacao.itens || [];
  const motivos = motivosDaSolicitacao(blocos, ehAssistencia);
  // O número do lote é texto livre digitado pelo gestor ("410", "376-05"); a
  // palavra "LOTE" é do rótulo, não do dado. Se alguém digitar "LOTE 410",
  // tiramos o prefixo para o comprovante impresso não sair "LOTE LOTE 410".
  const lote = String(loteNumero ?? "")
    .trim()
    .replace(/^lote\b\s*[:\-—]?\s*/i, "");
  return (
    <ComprovanteCard ref={cardRef}>
      <CompMarca>PharuERP · Solicitação interna</CompMarca>
      <CompNumero>
        {lote && <span className="lote">LOTE {lote}</span>}
        <span className="num">{solicitacao.numero}</span>
      </CompNumero>
      <CompLinhas>
        <LinhaDado rotulo="Cliente" valor={solicitacao.cliente_nome || "—"} />
        <LinhaDado rotulo="Pedido" valor={solicitacao.num_pedido || "—"} />
        <LinhaDado rotulo="Supervisor" valor={solicitacao.supervisor_nome} />
        <LinhaDado
          rotulo="Líder de montagem"
          valor={solicitacao.lider_montagem_nome}
        />
        <LinhaDado rotulo="Setor" valor={solicitacao.setor} />
        <LinhaDado rotulo="Lote de produção" valor={solicitacao.lote_producao} />
        <LinhaDado
          rotulo="Solicitante"
          valor={solicitacao.criado_por_nome || solicitanteFallback || "—"}
        />
        <LinhaDado
          rotulo="Data/hora"
          valor={formatarDataHora(solicitacao.created_at)}
        />
      </CompLinhas>

      <CompPecas>
        {blocos.map((bloco, i) =>
          ehAssistencia ? (
            <BlocoPeca key={bloco.id || i} peca={bloco} />
          ) : (
            <BlocoItem key={bloco.id || i} item={bloco} />
          ),
        )}
      </CompPecas>

      {motivos.length > 0 && (
        <CompMotivo>
          <span className="rotulo">
            {motivos.length > 1
              ? "Motivos da solicitação"
              : "Motivo da solicitação"}
          </span>
          {motivos.map((m) => (
            <span className="valor" key={m}>
              {motivos.length > 1 ? `• ${m}` : m}
            </span>
          ))}
        </CompMotivo>
      )}

      <CompRodape>
        <span>Gerado pelo PharuERP</span>
        <span>{formatarDataHora(new Date().toISOString())}</span>
      </CompRodape>
    </ComprovanteCard>
  );
}
